import { type FormEvent, useId, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { isProblemError } from '../../api/client';
import {
  getListMaterialsQueryKey,
  useCreateMaterial,
  useCreateMaterialDownloadURL,
  useDeleteMaterial,
  useListMaterials,
} from '../../api/generated/academic/academic';
import { useGetMe } from '../../api/generated/identity/identity';
import type { Material, MaterialVisibility, Me } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { ConfirmDialog } from '../../design/primitives/Dialog';
import { Field } from '../../design/primitives/Field';
import { TextAreaField } from '../academic/TextAreaField';
// A pilha de cursores ja existe na SPA e a regra e a mesma aqui: cada pagina
// devolve `nextCursor` e nada devolve cursor de volta. Uma segunda pilha nesta
// tela seria um segundo lugar onde "voltar" pode discordar.
import { usePager } from '../enrollment/AdminOnly';
// Erro e vazio nunca juntos: a regra mora num lugar so, e nao em cada tela.
import { ListOutcome } from '../enrollment/ListOutcome';
// Os bytes sobem por este arquivo, e nao por um `fetch` novo: a excecao da
// regra de lint que permite falar HTTP a mao esta declarada para ele sozinho,
// porque o destino e o armazenamento e nao a API. Escrever outro `fetch` aqui
// mandaria o cookie de sessao para um host que nao e o nosso.
import { uploadToSignedUrl } from '../messaging/signedUpload';
import { formatNumber } from '../../i18n/datetime';
import styles from '../academic/academic.module.css';

/** Limites que o contrato declara para `createMaterial`. */
const TITLE_MAX_LENGTH = 200;
const DESCRIPTION_MAX_LENGTH = 2000;

/**
 * Visibilidades **oferecidas na publicacao**, que nao sao todas as que o
 * material pode ter.
 *
 * `specific_students` existe no modelo e e lida na biblioteca, mas
 * `createMaterial` nao carrega a lista de alunos liberados: o vinculo vive em
 * `material_grant` e nao ha operacao de contrato que o escreva. Oferecer a
 * opcao aqui publicaria um material que ninguem alem do autor consegue abrir —
 * uma escolha que so pode dar errado. A lacuna esta no relatorio.
 */
const OFFERED_VISIBILITIES: MaterialVisibility[] = ['all_students', 'by_level'];

/**
 * Biblioteca de materiais. A URL assinada e pedida **na hora do clique** e
 * usada imediatamente — nunca guardada em estado nem em cache, porque URL
 * assinada em cache e URL assinada vazando por historico de navegador.
 */
export function MaterialLibrary() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;
  // O acervo NAO cabe numa resposta: `listMaterials` ordena do mais recente
  // para o mais antigo e trunca a pagina (`material/store/queries.sql`,
  // `material/page.go`). Pedir sem cursor e ler `items` era prometer a
  // biblioteca e entregar o ultimo mes dela.
  const pager = usePager();
  const materialsQuery = useListMaterials(pager.cursor === undefined ? {} : { cursor: pager.cursor }, {
    query: { enabled: !!me },
  });

  if (!me) return null;

  const materials = materialsQuery.data?.items ?? [];
  const nextCursor = materialsQuery.data?.nextCursor ?? null;
  // Quem publica e quem apaga sao decididos por criterios diferentes: publicar
  // e questao de papel, apagar e questao de autoria (ver `MaterialCard`).
  const canPublish = me.role === 'teacher' || me.role === 'super_admin';

  return (
    <div className={styles.page}>
      <h1>{t('materials.title')}</h1>
      {canPublish ? <PublishForm onPublished={() => pager.reset()} /> : null}

      {/*
        O estado da BIBLIOTECA fica junto da biblioteca, e nao acima do
        formulario: o alerta de falha de leitura logo abaixo do "Publicar" se
        lia como recusa da publicacao.
      */}
      <ListOutcome
        isPending={materialsQuery.isPending}
        isError={materialsQuery.isError}
        count={materials.length}
        empty={t('materials.empty')}
      />

      <ul className={styles.list}>
        {materials.map((material) => (
          <MaterialCard
            key={material.id}
            material={material}
            me={me}
            onRemoved={() => void materialsQuery.refetch()}
          />
        ))}
      </ul>

      {/*
        A navegacao existe mesmo na unica pagina, com os dois controles
        desabilitados: um botao que aparece so quando ha mais some justamente
        quando alguem procura por ele, e some sem dizer que nao ha mais nada.
        O rotulo vem do catalogo de cadastro porque e o mesmo par de frases
        neutras — ver o relatorio, `fronteira`.
      */}
      <nav className={styles.row} aria-label={t('materials.title')}>
        <Button disabled={!pager.canGoBack} onClick={pager.goBack}>
          {t('enrollment.previousPage')}
        </Button>
        <Button
          disabled={nextCursor === null}
          onClick={() => {
            if (nextCursor !== null) pager.goForward(nextCursor);
          }}
        >
          {t('enrollment.nextPage')}
        </Button>
      </nav>
    </div>
  );
}

/**
 * Publicar material.
 *
 * A biblioteca so lia, baixava e apagava: `createMaterial` e normativa no
 * contrato e nao tinha chamador nenhum na SPA, entao professor e Super Admin
 * nao tinham por onde publicar — e nao existe outro caminho fora desta tela.
 * A parte destrutiva estava completa e a construtiva nao existia.
 *
 * O envio tem **duas etapas e as duas contam**: `createMaterial` registra a
 * linha e devolve a URL assinada, e so entao os bytes sobem. Se a segunda
 * falhar, o material JA existe na lista sem conteudo atras dele — e por isso o
 * texto de erro dos dois casos e diferente: um diz que nada foi publicado, o
 * outro diz que ha uma linha a apagar. Colapsa-los deixaria a pessoa sem saber
 * se precisa limpar alguma coisa antes de tentar de novo.
 */
function PublishForm({ onPublished }: { readonly onPublished: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const create = useCreateMaterial();
  const visibilityId = useId();
  const fileId = useId();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<MaterialVisibility>('all_students');
  const [levelCode, setLevelCode] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [published, setPublished] = useState(false);
  /** Verdadeiro entre o `create` e o fim do upload: o botao gira nos dois. */
  const [uploading, setUploading] = useState(false);

  const validate = (): string | null => {
    if (title.trim().length === 0) return t('materials.titleRequired');
    if (file === null) return t('materials.pickFile');
    // `by_level` sem codigo publica um material que nenhuma consulta alcanca:
    // o servidor casa o nivel do aluno com este campo, e nulo nao casa nada.
    if (visibility === 'by_level' && levelCode.trim().length === 0) {
      return t('materials.levelRequired');
    }
    return null;
  };

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const problem = validate();
    setPublished(false);
    if (problem !== null || file === null) {
      setError(problem);
      return;
    }
    setError(null);
    setUploading(true);

    create.mutate(
      {
        data: {
          title: title.trim(),
          description: description.trim().length === 0 ? null : description.trim(),
          mimeType: file.type,
          sizeBytes: file.size,
          visibility,
          levelCode: visibility === 'by_level' ? levelCode.trim() : null,
        },
      },
      {
        onSuccess: async (created) => {
          try {
            await uploadToSignedUrl(created.upload.url, file);
          } catch {
            // A linha ficou; o arquivo nao. O texto precisa dizer isso, porque
            // o material aparece na lista e parece publicado.
            setError(t('materials.uploadFailed'));
            setUploading(false);
            void queryClient.invalidateQueries({ queryKey: getListMaterialsQueryKey() });
            return;
          }
          setUploading(false);
          setPublished(true);
          setTitle('');
          setDescription('');
          setLevelCode('');
          setFile(null);
          // Volta a primeira pagina antes de reler: a biblioteca ordena do mais
          // recente para o mais antigo, e o material que acabou de nascer esta
          // la — nao na pagina em que quem publicou estava parado.
          onPublished();
          void queryClient.invalidateQueries({ queryKey: getListMaterialsQueryKey() });
        },
        onError: (failure) => {
          setUploading(false);
          // 403 e "voce nao publica nesta biblioteca", e nao um contratempo:
          // repetir devolve a mesma resposta.
          setError(
            isProblemError(failure) && failure.status === 403
              ? t('materials.publishForbidden')
              : t('materials.createFailed'),
          );
        },
      },
    );
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <h2 className={styles.subheading}>{t('materials.publish')}</h2>

      <Field
        label={t('materials.fieldTitle')}
        name="title"
        required
        value={title}
        maxLength={TITLE_MAX_LENGTH}
        onChange={(event) => setTitle(event.target.value)}
      />

      <TextAreaField
        label={t('materials.fieldDescription')}
        value={description}
        maxLength={DESCRIPTION_MAX_LENGTH}
        onChange={setDescription}
      />

      <label className={styles.selectLabel} htmlFor={visibilityId}>
        {t('materials.fieldVisibility')}
        <select
          id={visibilityId}
          value={visibility}
          onChange={(event) => setVisibility(event.target.value as MaterialVisibility)}
        >
          {OFFERED_VISIBILITIES.map((option) => (
            <option key={option} value={option}>
              {t(`materials.visibility.${option}`)}
            </option>
          ))}
        </select>
      </label>

      {visibility === 'by_level' ? (
        <Field
          label={t('materials.fieldLevel')}
          name="levelCode"
          required
          value={levelCode}
          onChange={(event) => setLevelCode(event.target.value)}
        />
      ) : null}

      <label className={styles.selectLabel} htmlFor={fileId}>
        {t('materials.fieldFile')}
        <input
          id={fileId}
          type="file"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />
      </label>

      <Button type="submit" variant="primary" loading={create.isPending || uploading}>
        {t('materials.submit')}
      </Button>

      {error ? <Alert tone="error">{error}</Alert> : null}
      {published ? <Alert tone="success">{t('materials.published')}</Alert> : null}
    </form>
  );
}

/**
 * Traduz a recusa da emissao de URL assinada para o texto que decide o que
 * fazer em seguida.
 *
 * `createMaterialDownloadURL` declara 403 e 404 no contrato, e o servidor da os
 * dois de verdade: 403 quando a visibilidade do material nao alcanca quem pediu
 * (`ErrNoAccess`, `material/service.go`) e 404 quando ele nao esta mais na
 * biblioteca (`ErrNotFound`). Nenhuma das duas muda se a pessoa clicar de novo,
 * e e por isso que nao podem virar "tente de novo em instantes".
 *
 * Antes disto nao havia estado nenhum: `download.isError` nao era lido em lugar
 * nenhum do arquivo, entao a recusa era um botao que parava de girar e um DOM
 * identico ao de antes do clique — indistinguivel de um clique que nao
 * registrou. A tela irma das gravacoes ja separa as tres recusas dela
 * (`RecordingsPage.refusalKey`), e esta e a mesma forma.
 */
function downloadRefusalKey(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (!isProblemError(error)) return 'error.unexpected';
  if (error.status === 403) return 'materials.downloadDenied';
  if (error.status === 404) return 'materials.gone';
  return 'error.unexpected';
}

function MaterialCard({
  material,
  me,
  onRemoved,
}: {
  readonly material: Material;
  readonly me: Me;
  readonly onRemoved: () => void;
}) {
  const { t } = useTranslation();
  const download = useCreateMaterialDownloadURL();
  const remove = useDeleteMaterial();
  const [confirming, setConfirming] = useState(false);

  /**
   * Apagar e questao de AUTORIA, e nao de papel.
   *
   * `DeleteMaterial` recusa quem nao e o autor (`material/service.go`: "So o
   * autor ou Super Admin"). Decidir por papel oferecia a acao a todo professor
   * sobre todo material da biblioteca — inclusive os de outros professores e os
   * do Super Admin. A pessoa confirmava um dialogo que promete tirar o material
   * "da biblioteca de todo aluno de uma vez" e recebia "erro inesperado", sem
   * descobrir que a barreira era a autoria.
   *
   * `author.personId` chega nulo quando o servidor omite a autoria — e ai a
   * comparacao e falsa, que e a resposta certa: sem saber quem publicou, nao se
   * oferece a acao que exige ser quem publicou.
   */
  const canRemove = me.role === 'super_admin' || material.author.personId === me.id;

  const downloadRefusal = downloadRefusalKey(download.error);

  const size =
    material.sizeBytes >= 1048576
      ? `${formatNumber(material.sizeBytes / 1048576, me.locale, { maximumFractionDigits: 1 })} MB`
      : `${formatNumber(Math.ceil(material.sizeBytes / 1024), me.locale)} KB`;

  return (
    // Nomear o material e o que permite falar de *um* deles: sem isso, uma
    // asercao sobre "Apagar" casa com o botao de qualquer linha da lista.
    <li className={styles.card} aria-label={material.title}>
      <p>
        <strong>{material.title}</strong>
      </p>
      {material.description ? <p>{material.description}</p> : null}
      <p className={styles.meta}>
        {material.mimeType} · {size} · {t(`materials.visibility.${material.visibility}`)}
      </p>

      {remove.isError ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}
      {/*
        A recusa do download mora na LINHA, como a da remocao logo acima: um
        alerta no topo da pagina nao diria de qual material ele fala, e a
        biblioteca mostra ate cinquenta por vez.
      */}
      {downloadRefusal ? <Alert tone="warning">{t(downloadRefusal)}</Alert> : null}

      <div className={styles.row}>
        <Button
          variant="secondary"
          loading={download.isPending}
          onClick={() =>
            download.mutate(
              { materialId: material.id },
              {
                onSuccess: (signed) => {
                  // Abre na hora e descarta: a URL nunca vai para estado nem cache.
                  window.open(signed.url, '_blank', 'noopener,noreferrer');
                },
              },
            )
          }
        >
          {t('materials.download')}
        </Button>

        {canRemove ? (
          <Button variant="danger" onClick={() => setConfirming(true)}>
            {t('materials.delete')}
          </Button>
        ) : null}
      </div>

      {/*
        Remocao e destrutiva e nao volta: o material sai da biblioteca de todo
        aluno de uma vez. A confirmacao diz o **titulo** do material, porque
        "Apagar material?" nao ajuda quem clicou na linha errada.
      */}
      <ConfirmDialog
        open={confirming}
        title={t('materials.deleteConfirmTitle')}
        description={t('materials.deleteConfirmBody', { title: material.title })}
        confirmLabel={t('materials.delete')}
        loading={remove.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() =>
          remove.mutate(
            { materialId: material.id },
            {
              onSuccess: () => {
                setConfirming(false);
                onRemoved();
              },
              onError: () => setConfirming(false),
            },
          )
        }
      />
    </li>
  );
}
